import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Redirect } from 'react-router-dom';
import lodashOmit from 'lodash/fp/omit';
import lodashPick from 'lodash/fp/pick';
import lodashIsEqual from 'lodash/fp/isEqual';
import lodashTimes from 'lodash/fp/times';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import getListingFormFields from 'common/listingFormFields';
import getTenantScreeningCriteriaFormFields from 'common/tenantScreeningCriteriaFormFields';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';

import { SR_USER_GROUP_ROLE_PARTNER } from 'common/userRoles';

import { getListingFullAddressString, isListingTenantScreening } from 'common/listingHelpers';

import { cleanMarketingPartnerListingFormValues } from 'common/marketingPartnerListingHelpers';

import {
  SR_API_ACTION_PARTNER_EDIT_LISTING,
  SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
  SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
} from 'api/actionTypes';

import {
  partnerEditListingFieldgroup,
  petPolicyFieldgroup,
} from 'server/models/Listing/fieldgroups';

import { SCREEN_ID_PARTNER_LISTING_EDIT_RENTER_PET_CRITERIA } from 'client/constants/screenIds';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_FONT_SIZE_SMALL,
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
  SR_FIELD_THEME_ADOBE_LONG_LABEL,
} from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';
import SrAccountManagerContactSupport from 'client/components/SrAccountManagerContactSupport/SrAccountManagerContactSupport';
import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrBreedRestrictionsModal from 'client/components/SrBreedRestrictionsModal';

import PartnerActions from 'client/actions/Partner';
import AccountManagerActions from 'client/actions/AccountManager';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

const FormikContext = React.createContext();

const tenantScreeningCriteriaFieldgroup = [
  'creditScore',
  'creditScoreQualification',
  'rentToIncomeRatio',
  'moveInDateRangeInDays',
  'feloniesAccepted',
  'feloniesExpirationInYears',
  'bankruptciesAccepted',
  'bankruptciesExpirationInYears',
  'evictionsAccepted',
  'evictionsExpirationInYears',
  'guarantorAccepted',
  'guarantorRentToIncomeRatio',
  'verifyRentalHistory',
  'verifyRentalHistoryOnMinCreditScore',
];

const pickTenantScreeningCriteriaFields = lodashPick(tenantScreeningCriteriaFieldgroup);
const pickListingFields = lodashPick(partnerEditListingFieldgroup);

const omitTenantScreeningCriteriaFields = lodashOmit([
  'id',
  'breedRestrictions',
  'feloniesExpirationInYears',
  'bankruptciesExpirationInYears',
  'evictionsExpirationInYears',
]);

const getFormFields = ({ formValues, formParams, setFieldValue }) => {
  const yearChoices = lodashTimes(
    (value) => ({
      value,
      label: value === 0 ? 'Any Time' : `${value}+ Years Ago`,
    }),
    8,
  );
  const tenantScreeningCriteriaFormFields = getTenantScreeningCriteriaFormFields({
    fieldgroup: tenantScreeningCriteriaFieldgroup,
    required: true,
    updates: {
      rentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = index * 0.25 + 2;
            return {
              value,
              label: `${value}x Rent` + (value === 3 ? ' (recommended)' : ''),
            };
          }, 19),
        },
      },
      feloniesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.feloniesAccepted,
      },
      bankruptciesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.bankruptciesAccepted,
      },
      evictionsExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.evictionsAccepted,
      },
      guarantorRentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = index * 0.25 + 3;
            return {
              value,
              label: `${value}x Rent` + (value === 3 ? ' (recommended)' : ''),
            };
          }, 15),
        },
        hideFieldIf: !formValues.guarantorAccepted,
      },
      verifyRentalHistory: {
        properties: {
          onChange: (verifyRentalHistory) => {
            if (!verifyRentalHistory && setFieldValue) {
              setFieldValue('verifyRentalHistoryOnMinCreditScore', '');
            }
          },
        },
      },
      verifyRentalHistoryOnMinCreditScore: {
        hideFieldIf: !formValues.verifyRentalHistory,
        properties: {
          noRequired: true,
        },
      },
    },
  });

  const shouldHidePetsField = !formValues.catsAllowed && !formValues.dogsAllowed;
  const { isTenantScreening } = formParams;
  const shouldHidePetDeposit = isTenantScreening || shouldHidePetsField;

  const listingFormFields = getListingFormFields({
    fieldgroup: petPolicyFieldgroup,
    required: true,
    updates: {
      catsAllowed: {
        type: 'yes_no',
      },
      dogsAllowed: {
        type: 'yes_no',
      },
      maximumPetsAllowed: {
        hideFieldIf: shouldHidePetsField,
      },
      petWeight: {
        title: 'Max Dog Weight',
        hideFieldIf: formValues.dogsAllowed !== true,
      },
    },
  });

  return [
    ...tenantScreeningCriteriaFormFields,
    ...listingFormFields,
    hideFieldIf(shouldHidePetDeposit, {
      id: 'petDeposit',
      title: 'Pet Deposit Amount',
      titleSecondary: '(Refundable)',
      type: 'currency',
      validations: ['required'],
    }),
    hideFieldIf(shouldHidePetDeposit, {
      id: 'petDepositPerPet',
      title: 'How is the deposit structured?',
      type: 'multiple_choice',
      validations: ['required'],
      properties: {
        choices: [
          {
            label: 'Per Pet',
            value: true,
          },
          {
            label: 'Covers All Pets',
            value: false,
          },
        ],
        multipleSelection: false,
        fieldType: SR_CHECKBOX_THEME_BOX,
        autoFitWidth: '265px',
      },
    }),
    hideFieldIf(shouldHidePetDeposit || !formValues.petDepositPerPet, {
      id: 'additionalPetDeposit',
      title: "What's the deposit per additional pet?",
      type: 'currency',
      validations: ['required'],
    }),
    hideFieldIf(shouldHidePetDeposit, {
      id: 'petFee',
      title: 'Pet Fee Amount',
      titleSecondary: '(Non-Refundable)',
      validations: ['required'],
      type: 'currency',
    }),
    hideFieldIf(shouldHidePetDeposit, {
      id: 'petFeePerPet',
      title: 'How is the fee structured?',
      type: 'multiple_choice',
      validations: ['required'],
      properties: {
        choices: [
          {
            label: 'Per Pet',
            value: true,
          },
          {
            label: 'Covers All Pets',
            value: false,
          },
        ],
        multipleSelection: false,
        fieldType: SR_CHECKBOX_THEME_BOX,
        autoFitWidth: '265px',
      },
    }),
    hideFieldIf(shouldHidePetDeposit || !formValues.petFeePerPet, {
      id: 'additionalPetFee',
      title: "What's the fee per additional pet?",
      validations: ['required'],
      type: 'currency',
    }),
    hideFieldIf(shouldHidePetDeposit, {
      id: 'monthlyPetRent',
      title: 'Monthly Pet Rent',
      titleSecondary: '(Non-Refundable)',
      type: 'currency',
      validations: ['required'],
    }),
    hideFieldIf(shouldHidePetDeposit, {
      id: 'monthlyPetRentPerPet',
      title: 'How is the monthly rent structured?',
      type: 'multiple_choice',
      validations: ['required'],
      properties: {
        choices: [
          {
            label: 'Per Pet',
            value: true,
          },
          {
            label: 'Covers All Pets',
            value: false,
          },
        ],
        multipleSelection: false,
        fieldType: SR_CHECKBOX_THEME_BOX,
        autoFitWidth: '265px',
      },
    }),
    hideFieldIf(shouldHidePetDeposit || !formValues.monthlyPetRentPerPet, {
      id: 'additionalMonthlyPetRent',
      title: "What's the monthly rent per additional pet?",
      validations: ['required'],
      type: 'currency',
    }),
  ];
};

const getListingDataTableSettings = ({ partner, match }) => {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
};

const getDefaultFormValues = ({
  listing,
  listingTenantScreeningCriteria,
  partnerTenantScreeningCriteria,
}) => {
  const tenantScreeningCriteria =
    listingTenantScreeningCriteria || partnerTenantScreeningCriteria || {};

  const defaultFormValues = {
    ...pickTenantScreeningCriteriaFields(tenantScreeningCriteria),
    ...lodashPick(
      partnerEditListingFieldgroup.filter((fieldId) => listing[fieldId] !== null),
      listing,
    ),
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInMonths === 'number') {
    defaultFormValues.feloniesExpirationInYears =
      tenantScreeningCriteria.feloniesExpirationInMonths / 12;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInMonths === 'number') {
    defaultFormValues.bankruptciesExpirationInYears =
      tenantScreeningCriteria.bankruptciesExpirationInMonths / 12; // eslint-disable-line max-len
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInMonths === 'number') {
    defaultFormValues.evictionsExpirationInYears =
      tenantScreeningCriteria.evictionsExpirationInMonths / 12;
  }

  return defaultFormValues;
};

const getTenantScreeningCriteriaPayload = ({
  listing,
  tenantScreeningCriteriaUpdates,
  listingTenantScreeningCriteria,
  partnerTenantScreeningCriteria,
}) => {
  const tenantScreeningCriteria = {
    ...tenantScreeningCriteriaUpdates,
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInYears === 'number') {
    tenantScreeningCriteria.feloniesExpirationInMonths =
      tenantScreeningCriteria.feloniesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.feloniesDescription =
      tenantScreeningCriteria.feloniesExpirationInYears > 0
        ? `in the past ${tenantScreeningCriteria.feloniesExpirationInYears} years`
        : null;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInYears === 'number') {
    tenantScreeningCriteria.bankruptciesExpirationInMonths =
      tenantScreeningCriteria.bankruptciesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.bankruptciesDescription =
      tenantScreeningCriteria.bankruptciesExpirationInYears > 0 // eslint-disable-line max-len
        ? `in the past ${tenantScreeningCriteria.bankruptciesExpirationInYears} years`
        : null;
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInYears === 'number') {
    tenantScreeningCriteria.evictionsExpirationInMonths =
      tenantScreeningCriteria.evictionsExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.evictionsDescription =
      tenantScreeningCriteria.evictionsExpirationInYears > 0
        ? `in the past ${tenantScreeningCriteria.evictionsExpirationInYears} years`
        : null;
  }

  // Create a new tenant screening criteria by listing
  if (!listingTenantScreeningCriteria) {
    return {
      listingId: listing.id,
      tenantScreeningCriteria: omitTenantScreeningCriteriaFields({
        ...partnerTenantScreeningCriteria,
        ...tenantScreeningCriteria,
      }),
    };
  }

  // Update current tenant screening criteria
  return {
    listingId: listing.id,
    tenantScreeningCriteria: omitTenantScreeningCriteriaFields({
      ...listingTenantScreeningCriteria,
      ...tenantScreeningCriteria,
    }),
  };
};

@withTransitionHook
@allowedRoles({
  roles: SR_USER_GROUP_ROLE_PARTNER,
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT_RENTER_PET_CRITERIA)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManager: ['accountManagers', 'byListingId', match.params.listingId],
  listingStripeAccount: ['listingStripeAccountsByListingId', match.params.listingId],
  listingTenantScreeningCriteria: [
    'tenantScreeningCriteria',
    'byListingId',
    match.params.listingId,
  ],
  listingBreedRestrictions: ['breedRestrictions', 'byListingId', match.params.listingId],
  breedRestrictionDefinitions: ['breedRestrictions', 'definitions'],
}))
@branch(({ partner }) => {
  if (partner) {
    return {
      partnerTenantScreeningCriteria: [
        'tenantScreeningCriteria',
        'byPartnerManagementCompany',
        partner.managementCompanyName,
      ],
    };
  }
  return {};
})
@dataTable(getListingDataTableSettings)
@withLocationQuery
class PartnerEditListingRenterPetCriteriaPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing | Renter & Pet Criteria',
  });

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const listingId = parseInt(match.params.listingId, 10);

    const promises = [
      AccountManagerActions.getByListingId(inject, { listingId }),
      PartnerActions.loadListingStripeAccount(inject, { listingId }),
      PartnerActions.loadBreedRestrictionDefinitions(inject),
      TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, { listingId }),
      TenantScreeningCriteriaActions.getBreedRestrictions(inject, {
        listingId,
        withPartnerCascade: true,
      }),
    ];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    const listingDataTableSettings = getListingDataTableSettings({ partner, match });
    if (listingDataTableSettings) {
      actionInitializeDataTableState(inject, listingDataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, listingDataTableSettings));
    }

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

      return TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
        managementCompanyName: partner.managementCompanyName,
      });
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    listing: PropTypes.object,
    accountManager: PropTypes.object,
    isTransitioning: PropTypes.bool.isRequired,
  };

  constructor() {
    super();

    this.state = {
      isSubmitting: false,
      editSuccessModalIsOpen: false,
      showBreedRestrictionsModal: false,
    };

    this.petDepositRef = React.createRef(null);
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }

    setTimeout(() => {
      if (this.petDepositRef.current && location.search.includes('scrollTo=petDeposit')) {
        this.petDepositRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  }

  componentWillUnmount() {
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  _onSubmit = (formValues) => {
    this.setState({ isSubmitting: true });

    const {
      match,
      history,
      listing,
      partner,
      listingTenantScreeningCriteria,
      partnerTenantScreeningCriteria,
      locationQuery,
      dispatch,
    } = this.props;

    const defaultFormValues = getDefaultFormValues({
      listing,
      listingTenantScreeningCriteria,
      partnerTenantScreeningCriteria,
    });

    const tenantScreeningCriteriaUpdates = setNullEmptyValues(
      pickTenantScreeningCriteriaFields(formValues),
    );
    const listingUpdates = cleanMarketingPartnerListingFormValues(pickListingFields(formValues));

    const shouldUpdateTenantScreeningCriteria = !lodashIsEqual(
      pickTenantScreeningCriteriaFields(defaultFormValues),
      tenantScreeningCriteriaUpdates,
    );
    const shouldUpdateListing = !lodashIsEqual(
      pickListingFields(defaultFormValues),
      listingUpdates,
    );
    const breedRestrictionsRemoved = !listingUpdates.dogsAllowed;

    const actionRequests = [
      shouldUpdateListing &&
        dispatch(postWithState, {
          action: SR_API_ACTION_PARTNER_EDIT_LISTING,
          payload: {
            partnerId: partner.id,
            listingId: listing.id,
            updates: listingUpdates,
          },
        }),
      shouldUpdateTenantScreeningCriteria &&
        dispatch(postWithState, {
          action: SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
          payload: getTenantScreeningCriteriaPayload({
            listing,
            tenantScreeningCriteriaUpdates,
            listingTenantScreeningCriteria,
            partnerTenantScreeningCriteria,
          }),
        }),
      breedRestrictionsRemoved && this._onSaveBreedRestrictions([]),
    ];

    Promise.all(lodashCompact(actionRequests))
      .then(() => {
        return Promise.all(
          lodashCompact([
            shouldUpdateListing &&
              dispatch(
                actionDataTableLoadItems,
                getListingDataTableSettings({
                  partner,
                  match,
                }),
              ),
            shouldUpdateTenantScreeningCriteria &&
              dispatch(TenantScreeningCriteriaActions.getTenantScreeningCriteria, {
                // eslint-disable-line max-len
                listingId: listing.id,
                reload: true,
              }),
          ]),
        );
      })
      .then(() => {
        if (locationQuery.householdListingIdToApprove) {
          history.push(
            `/partner/${match.params.partnerSlug}/listing/${match.params.listingId}/approve/${locationQuery.householdListingIdToApprove}/summary`,
          ); // eslint-disable-line max-len
        } else {
          this.setState({
            isSubmitting: false,
            editSuccessModalIsOpen: true,
          });
        }
      });
  };

  _onCloseEditSuccessModal = () => {
    this.setState({
      editSuccessModalIsOpen: false,
    });
  };

  _onOpenBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: true,
    });
  };

  _onCloseBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: false,
    });
  };

  _onSaveBreedRestrictions = (breedRestrictions) => {
    const {
      listing,
      listingTenantScreeningCriteria,
      partnerTenantScreeningCriteria,
      dispatch,
    } = this.props;

    const defaultFormValues = getDefaultFormValues({
      listing,
      listingTenantScreeningCriteria,
      partnerTenantScreeningCriteria,
    });

    const tenantScreeningCriteriaUpdates = setNullEmptyValues(
      pickTenantScreeningCriteriaFields(defaultFormValues),
    );

    this._onCloseBreedRestrictionsModal();

    dispatch(postWithState, {
      action: SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
      payload: getTenantScreeningCriteriaPayload({
        listing,
        tenantScreeningCriteriaUpdates,
        listingTenantScreeningCriteria,
        partnerTenantScreeningCriteria,
      }),
    }).then(() => {
      dispatch(postWithState, {
        action: SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
        payload: {
          listingId: listing.id,
          breedRestrictions,
        },
        onSuccess: () => {
          dispatch(TenantScreeningCriteriaActions.getBreedRestrictions, {
            listingId: listing.id,
            withPartnerCascade: true,
            reload: true,
          });
        },
      });
    });
  };

  _shouldHidePetDepositFees = (formValues) => {
    const { listing } = this.props;
    const { catsAllowed, dogsAllowed } = formValues;
    const isTenantScreening = isListingTenantScreening(listing);

    return isTenantScreening || (!catsAllowed && !dogsAllowed);
  };

  render() {
    const {
      listing,
      partner,
      currentUser,
      accountManager,
      isTransitioning,
      listingTenantScreeningCriteria,
      listingBreedRestrictions,
      breedRestrictionDefinitions,
      partnerTenantScreeningCriteria,
      dispatch,
    } = this.props;

    const { isSubmitting, editSuccessModalIsOpen, showBreedRestrictionsModal } = this.state;

    if (isTransitioning && (!partner || !listing)) {
      return null;
    }

    if (!partner || !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const defaultFormValues = getDefaultFormValues({
      listing,
      listingTenantScreeningCriteria,
      partnerTenantScreeningCriteria,
      listingBreedRestrictions,
    });

    const isTenantScreening = isListingTenantScreening(listing);
    const breedRestrictions = listingBreedRestrictions || [];
    const formParams = {
      isTenantScreening: isListingTenantScreening(listing),
    };

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/listing/${listing.id}/edit`}
        navbarTitle={listing ? getListingFullAddressString(listing) : null}
        pageHeader="Renter &amp; Pet Criteria"
      >
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: getFormFields({ formValues: defaultFormValues, formParams }),
            defaultValues: defaultFormValues,
          })}
          enableReinitialize
          validate={(formValues) => {
            const formFields = getFormFields({ formValues, formParams });
            return createFormValidationFn(formFields)(formValues);
          }}
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formValues = formikBag.values;
            const formFields = getFormFields({
              formValues,
              formParams,
              setFieldValue: formikBag.setFieldValue,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            this.dirty = formikBag.dirty;

            const shouldHideBreedRestrictionsField = !formValues.dogsAllowed;
            const shouldHidePetDepositFees = this._shouldHidePetDepositFees(formValues);

            return (
              <>
                <FormikContext.Provider value={formikBag}>
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header="Renter Qualifications"
                      subheader={
                        'All renters will complete credit, background & eviction checks. ' +
                        'Sunroom verifies rental and employment history. '
                      }
                    >
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('creditScore')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('creditScoreQualification')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('rentToIncomeRatio')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('moveInDateRangeInDays')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('feloniesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('feloniesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow>
                        <SrText
                          weight="600"
                          font="greycliff"
                          size="16px"
                          color="#132630"
                          paddingTop="18px"
                        >
                          In accordance with Fair Housing guidance, Sunroom does not restrict
                          applicants based on criminal history. Applications will include a
                          background check which can be reviewed in your decision process.
                        </SrText>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('bankruptciesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('bankruptciesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('evictionsAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('evictionsExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('guarantorAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('guarantorRentToIncomeRatio')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('verifyRentalHistory')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />

                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('verifyRentalHistoryOnMinCreditScore')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                    </SrCard>

                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                      header="Pet Policy"
                    >
                      {petPolicyFieldgroup.map((fieldId) => (
                        <SrGridRow key={fieldId} autofit autofitMinColumnWidth="185px">
                          <SrFormikField
                            formField={getFormFieldsById(fieldId)}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      ))}
                    </SrCard>
                    {!shouldHidePetDepositFees && (
                      <SrCard
                        ref={this.petDepositRef}
                        color={SR_CARD_BULLET_COLOR_PURPLE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        headerSizeType={SR_CARD_FONT_SIZE_SMALL}
                        header="Pet Deposit & Fees"
                      >
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petDeposit')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('petDepositPerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalPetDeposit')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petFee')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('petFeePerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalPetFee')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('monthlyPetRent')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('monthlyPetRentPerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalMonthlyPetRent')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                      </SrCard>
                    )}

                    {!shouldHideBreedRestrictionsField && (
                      <SrCard color={SR_CARD_BULLET_COLOR_PURPLE} header="Breed Restrictions">
                        <SrBox marginTop="20px">
                          <SrText
                            font="greycliff"
                            fontSize="21px"
                            lineHeight="29px"
                            weight="800"
                            display="block"
                          >
                            Choose dog breeds that are not allowed.
                          </SrText>
                          <SrBox bgColor="yellow-lighter" margin="10px -20px" padding="10px 20px">
                            <SrText font="greycliff" fontSize="16px" weight="800" display="block">
                              {breedRestrictions.length > 0
                                ? `${breedRestrictions.length}
                                  ${pluralize('breed', breedRestrictions.length)} restricted`
                                : 'No breeds restricted'}
                            </SrText>
                          </SrBox>
                          <SrGridRow autofit autofitMinColumnWidth="260px">
                            {breedRestrictions.map(({ name, id }) => (
                              <SrBox marginTop="-12px">
                                <SrFormikField
                                  key={id}
                                  formField={{
                                    type: 'multiple_choice',
                                    properties: {
                                      multipleSelection: false,
                                      useArrayValues: false,
                                      choices: [
                                        {
                                          label: name,
                                          value: id,
                                        },
                                      ],
                                      checkboxIconType: 'cancelIconRed',
                                    },
                                  }}
                                  value={id}
                                  theme={SR_FIELD_THEME_ADOBE}
                                />
                              </SrBox>
                            ))}
                          </SrGridRow>
                          <SrBox marginTop="20px" />
                          <SrLabel
                            label="See All Breeds"
                            color="blue300"
                            borderColor="#007AFF"
                            alignCenter
                            justifyCenter
                            onClick={this._onOpenBreedRestrictionsModal}
                          />
                        </SrBox>
                      </SrCard>
                    )}

                    <SrBox display="none">
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_PURPLE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Breed Restrictions"
                      >
                        <SrBox marginTop="20px">
                          <SrText
                            font="greycliff"
                            fontSize="21px"
                            lineHeight="29px"
                            weight="800"
                            display="block"
                          >
                            Choose dog breeds that are not allowed.
                          </SrText>
                          <SrBox bgColor="yellow-lighter" margin="10px -20px" padding="10px 20px">
                            <SrText font="greycliff" fontSize="16px" weight="800" display="block">
                              {breedRestrictions.length > 0
                                ? `${breedRestrictions.length}
                                ${pluralize('breed', breedRestrictions.length)} restricted`
                                : 'No breeds restricted'}
                            </SrText>
                          </SrBox>
                          <SrGridRow autofit autofitMinColumnWidth="260px">
                            {breedRestrictions.map(({ name, id }) => (
                              <SrBox marginBottom="-12px">
                                <SrFormikField
                                  key={id}
                                  formField={{
                                    type: 'multiple_choice',
                                    properties: {
                                      multipleSelection: false,
                                      useArrayValues: false,
                                      choices: [
                                        {
                                          label: name,
                                          value: id,
                                        },
                                      ],
                                      checkboxIconType: 'cancelIconRed',
                                    },
                                  }}
                                  value={id}
                                  theme={SR_FIELD_THEME_ADOBE}
                                />
                              </SrBox>
                            ))}
                          </SrGridRow>
                          <SrBox marginTop="20px" />
                          <SrLabel
                            label="See All Breeds"
                            color="blue300"
                            borderColor="#007AFF"
                            alignCenter
                            justifyCenter
                            onClick={this._onOpenBreedRestrictionsModal}
                          />
                        </SrBox>
                      </SrCard>
                    </SrBox>

                    {accountManager && (
                      <SrAccountManagerContactSupport accountManager={accountManager} />
                    )}

                    <SrStaticFooter>
                      <SrText
                        display="block"
                        lineHeight="25px"
                        font="greycliff"
                        color="grey400"
                        fontSize="13px"
                        weight="500"
                      >
                        {!isTenantScreening ? 'Updates can take 24 hours to syndicate' : ''}
                      </SrText>
                      <SrFooterButton hasBoxShadow={!isSubmitting} disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </SrFooterButton>
                    </SrStaticFooter>
                  </Form>
                </FormikContext.Provider>
                <SrRouteLeavingGuard when={formikBag.dirty && !isSubmitting} dispatch={dispatch} />
              </>
            );
          }}
        />

        {editSuccessModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmarkSuccessGreen.svg"
            headline={
              <span>
                Successfully <br /> edited property!
              </span>
            }
            message="Changes are live instantly on Sunroom."
            buttonText="ok, thanks"
            maxWidth={375}
            onClose={this._onCloseEditSuccessModal}
          />
        )}

        {showBreedRestrictionsModal && (
          <SrBreedRestrictionsModal
            breedRestrictions={breedRestrictions?.map((breed) => breed.name) || []}
            breedRestrictionDefinitions={
              breedRestrictionDefinitions?.map((breed) => breed.name) || []
            }
            onClose={this._onCloseBreedRestrictionsModal}
            onSave={this._onSaveBreedRestrictions}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingRenterPetCriteriaPage.displayName = 'PartnerEditListingRenterPetCriteriaPage';

export default PartnerEditListingRenterPetCriteriaPage;
